/**
 * 通知列表与未读数。
 *
 * 实时推送是 Phase 3（SSE）；现在用轮询，60 秒一次 + 窗口聚焦时刷新。
 * 未读数与列表走同一套后端过滤器，所以不会出现「红点说有，点进去是空的」。
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { notificationsApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Notification, NotificationFilter, Page } from '@/api/types'

const POLL_MS = 60_000

export function useNotifications(filter: NotificationFilter = 'all') {
  return useInfiniteQuery({
    queryKey: keys.notifications(filter),
    queryFn: ({ pageParam }) => notificationsApi.list(filter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<Notification>) => last.next_cursor ?? undefined,
  })
}

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: keys.unreadCount(),
    queryFn: () => notificationsApi.unreadCount(),
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}

export function useMarkRead() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (upToId?: string) => notificationsApi.markRead(upToId),
    onSuccess: (result) => {
      client.setQueryData(keys.unreadCount(), { count: result.count, truncated: result.truncated })
      client.invalidateQueries({ queryKey: keys.notificationsRoot() })
    },
  })
}

/** 未读数的显示文本：到上限就是 99+ */
export function unreadLabel(count: number, truncated: boolean): string {
  return truncated ? `${count}+` : String(count)
}
